import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import SectionHeader from '../components/SectionHeader';
import Button from '../components/Button';
import contactBg from '../assets/images/contact-bg.jpg';

const ContactSection = styled.main`
  min-height: 100vh;
  background: linear-gradient(rgba(18, 25, 16, 0.9), rgba(18, 25, 16, 0.9)), url(${contactBg});
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  padding: 120px 0 80px;
`;

const ContactContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 50px;
  
  @media (max-width: 991px) {
    grid-template-columns: 1fr;
  }
`;

const ContactFormContainer = styled.div`
  background-color: rgba(30, 43, 15, 0.8);
  border: 1px solid var(--primary-color);
  border-radius: 8px;
  padding: 30px;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
  opacity: 0;
  transform: translateY(30px);
  transition: opacity 0.6s ease, transform 0.6s ease;
  
  &.animate {
    opacity: 1;
    transform: translateY(0);
  }
`;

const ContactForm = styled.form`
  display: flex;
  flex-direction: column;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 5px;
  font-weight: 600;
  color: var(--secondary-color);
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  background-color: rgba(18, 25, 16, 0.8);
  border: 1px solid var(--primary-color);
  border-radius: 4px;
  color: var(--text-color);
  font-family: inherit;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: var(--secondary-color);
    box-shadow: 0 0 5px rgba(188, 179, 113, 0.5);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px;
  background-color: rgba(18, 25, 16, 0.8);
  border: 1px solid var(--primary-color);
  border-radius: 4px;
  color: var(--text-color);
  font-family: inherit;
  transition: all 0.3s ease;
  height: 150px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: var(--secondary-color);
    box-shadow: 0 0 5px rgba(188, 179, 113, 0.5);
  }
`;

const SubmitButton = styled(Button)`
  align-self: flex-start;
  margin-top: 10px;
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 30px;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  background-color: rgba(30, 43, 15, 0.8);
  border: 1px solid var(--primary-color);
  border-radius: 8px;
  padding: 20px;
  transition: all 0.3s ease;
  opacity: 0;
  transform: translateX(30px);
  transition: opacity 0.6s ease, transform 0.6s ease;
  transition-delay: ${props => props.delay || '0s'};
  
  &.animate {
    opacity: 1;
    transform: translateX(0);
  }
  
  &:hover {
    transform: translateX(10px);
    border-color: var(--secondary-color);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  }
  
  @media (max-width: 991px) {
    &:hover {
      transform: none;
    }
  }
`;

const InfoIcon = styled.div`
  font-size: 2rem;
  color: var(--secondary-color);
  margin-right: 20px;
`;

const InfoText = styled.div`
  h4 {
    color: var(--secondary-color);
    margin-bottom: 5px;
  }
`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  const contactInfo = [
    {
      id: 1,
      icon: 'fas fa-map-marker-alt',
      title: 'Localização',
      text: 'São Paulo, SP - Brasil',
      delay: '0s'
    },
    {
      id: 2,
      icon: 'fas fa-envelope',
      title: 'Email',
      text: 'contato@devteamsix.com.br',
      delay: '0.2s'
    },
    {
      id: 3,
      icon: 'fas fa-phone-alt',
      title: 'Telefone',
      text: '+55 (11) 99999-6666',
      delay: '0.4s'
    }
  ];
  
  useEffect(() => {
    const handleScroll = () => {
      const formContainer = document.querySelector('.form-container');
      const infoItems = document.querySelectorAll('.info-item');
      
      if (formContainer) {
        const position = formContainer.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        
        if (position.top < windowHeight * 0.75) {
          formContainer.classList.add('animate');
        }
      }
      
      infoItems.forEach(item => {
        const position = item.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        
        if (position.top < windowHeight * 0.75) {
          item.classList.add('animate');
        }
      });
    };
    
    window.addEventListener('scroll', handleScroll);
    // Initial check
    handleScroll();
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  
  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Nome é obrigatório';
    }
    
    if (!formData.email.trim()) {
      errors.email = 'Email é obrigatório';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email inválido';
    }
    
    if (!formData.subject.trim()) {
      errors.subject = 'Assunto é obrigatório';
    }
    
    if (!formData.message.trim()) {
      errors.message = 'Mensagem é obrigatória';
    }
    
    return errors;
  };
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    const errors = validateForm();
    setFormErrors(errors);
    
    if (Object.keys(errors).length === 0) {
      // Here you would normally send the form data to a server
      console.log('Form data submitted:', formData);
      setIsSubmitted(true);
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
      
      // Reset submission status after 5 seconds
      setTimeout(() => {
        setIsSubmitted(false);
      }, 5000);
    }
  };
  
  return (
    <ContactSection>
      <div className="container">
        <SectionHeader title="Entre em Contato" highlight="Contato" />
        <ContactContent>
          <ContactFormContainer className="form-container">
            {isSubmitted ? (
              <div style={{ textAlign: 'center' }}>
                <h3 style={{ color: 'var(--secondary-color)' }}>Mensagem Enviada!</h3>
                <p>Obrigado por entrar em contato. Responderemos em breve.</p>
              </div>
            ) : (
              <ContactForm onSubmit={handleSubmit}>
                <FormGroup>
                  <Label htmlFor="name">Nome Completo</Label>
                  <Input 
                    type="text" 
                    id="name" 
                    name="name" 
                    placeholder="Seu nome" 
                    value={formData.name}
                    onChange={handleChange}
                  />
                  {formErrors.name && <p style={{ color: '#ff6b6b', fontSize: '0.9rem', marginTop: '5px' }}>{formErrors.name}</p>}
                </FormGroup>
                <FormGroup>
                  <Label htmlFor="email">Email</Label>
                  <Input 
                    type="email" 
                    id="email" 
                    name="email" 
                    placeholder="seu@email.com" 
                    value={formData.email}
                    onChange={handleChange}
                  />
                  {formErrors.email && <p style={{ color: '#ff6b6b', fontSize: '0.9rem', marginTop: '5px' }}>{formErrors.email}</p>}
                </FormGroup>
                <FormGroup>
                  <Label htmlFor="subject">Assunto</Label>
                  <Input 
                    type="text" 
                    id="subject" 
                    name="subject" 
                    placeholder="Assunto da mensagem" 
                    value={formData.subject}
                    onChange={handleChange}
                  />
                  {formErrors.subject && <p style={{ color: '#ff6b6b', fontSize: '0.9rem', marginTop: '5px' }}>{formErrors.subject}</p>}
                </FormGroup>
                <FormGroup>
                  <Label htmlFor="message">Mensagem</Label>
                  <TextArea 
                    id="message" 
                    name="message" 
                    placeholder="Descreva seu projeto ou dúvida" 
                    value={formData.message}
                    onChange={handleChange}
                  />
                  {formErrors.message && <p style={{ color: '#ff6b6b', fontSize: '0.9rem', marginTop: '5px' }}>{formErrors.message}</p>}
                </FormGroup>
                <SubmitButton type="submit">Enviar Mensagem</SubmitButton>
              </ContactForm>
            )}
          </ContactFormContainer>
          <ContactInfo>
            {contactInfo.map((info) => (
              <InfoItem 
                key={info.id} 
                className="info-item"
                delay={info.delay}
              >
                <InfoIcon>
                  <i className={info.icon}></i>
                </InfoIcon>
                <InfoText>
                  <h4>{info.title}</h4>
                  <p>{info.text}</p>
                </InfoText>
              </InfoItem>
            ))}
          </ContactInfo>
        </ContactContent>
      </div>
    </ContactSection>
  );
};

export default Contact;
